import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { usePreferences } from "../contexts/PreferencesContext";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, LogOut, Bell, Building2, Settings2, Sparkles, Clock, Lock, BarChart3, CreditCard, AlertTriangle } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Button } from "../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import PreferenciasModal from "./PreferenciasModal";
import AssinaturaModal from "./AssinaturaModal";
import NotificationDrawer from "./NotificationDrawer";
import ChangelogModal, { useChangelogBadge } from "./ChangelogModal";
import axios from "axios";
import { useAppearance } from "../contexts/AppearanceContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = [
  { code: "pt-BR", flag: "🇧🇷", label: "Português" },
  { code: "en-US", flag: "🇺🇸", label: "English" },
  { code: "es-ES", flag: "🇪🇸", label: "Español" },
];

export default function TopBar({ onMenuClick }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { prefs, updatePrefs } = usePreferences();
  const isOwner = user?.role === "owner";
  const isAdmin = user?.role === "admin";
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [assinaturaOpen, setAssinaturaOpen] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const hasNewChangelog = useChangelogBadge();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3b82f6";

  // Trial status
  const [trial, setTrial] = useState(null);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [tenantPricing, setTenantPricing] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);

  const fetchSubscriptionData = useCallback(() => {
    if (isOwner) return;
    axios.get(`${API}/stripe/my-subscription`, { withCredentials: true })
      .then(res => {
        if (res.data?.pricing) setTenantPricing(res.data.pricing);
        setSubscriptionData(res.data);
      })
      .catch(() => {});
  }, [isOwner]);

  useEffect(() => {
    if (isOwner) return;
    axios.get(`${API}/trial/status`, { withCredentials: true })
      .then(res => {
        const d = res.data;
        if (d.is_trial) {
          setTrial(d);
          if (d.expired && !d.blocked) {
            const dismissed = sessionStorage.getItem("trial_expired_dismissed");
            if (!dismissed) setShowExpiredModal(true);
          }
        }
      }).catch(() => {});
    fetchSubscriptionData();
  }, [fetchSubscriptionData]); // eslint-disable-line

  const isCancellingSubscription = subscriptionData?.subscription_status === "cancelling";
  const cancelDaysLeft = subscriptionData?.current_period_end
    ? Math.max(0, Math.ceil((new Date(subscriptionData.current_period_end) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  // Fetch unread count on mount and when SSE notification arrives
  const fetchCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/count`, { withCredentials: true });
      setUnreadCount(res.data.count || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 60000); // fallback poll
    return () => clearInterval(interval);
  }, [fetchCount]);

  useEffect(() => {
    const onSSE = (e) => {
      const detail = e.detail || {};
      if (detail.count !== undefined) setUnreadCount(detail.count);
      else fetchCount();
    };
    window.addEventListener("pipe4:notification", onSSE);
    return () => window.removeEventListener("pipe4:notification", onSSE);
  }, [fetchCount]);

  const changeLanguage = async (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem("pipe4_language", code);
    await updatePrefs({ language: code });
  };

  const initials = user?.name
    ? user.name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
    : "U";

  return (
    <>
      <header data-testid="top-bar" className="bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-6 sticky top-0 z-20" style={{ height: "5rem" }}>
        <div className="flex items-center gap-3">
          <button
            data-testid="mobile-menu-btn"
            onClick={onMenuClick}
            className="lg:hidden w-9 h-9 flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-500"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center gap-3">

          {/* Trial button — admin only */}
          {trial?.is_trial && isAdmin && (
            <button
              data-testid="trial-status-btn"
              onClick={() => window.open("/checkout", "_self")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${trial.expired ? "bg-rose-50 border border-rose-200 text-rose-600 hover:bg-rose-100" : "bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100"}`}
            >
              <Clock className="w-3.5 h-3.5" />
              {trial.expired
                ? `Trial expirado${trial.grace_days_left > 0 ? ` (${trial.grace_days_left} dias restantes)` : ""}`
                : `${trial.days_remaining} dias restantes`
              }
              <span className="hidden sm:inline relative ml-1 px-2 py-0.5 rounded-md text-white text-[10px] font-bold" style={{ backgroundColor: tc500 }}>
                <span className="absolute inset-0 rounded-md animate-ping opacity-30" style={{ backgroundColor: tc500 }} />
                Contratar
              </span>
            </button>
          )}

          {/* Cancelling subscription badge — admin only */}
          {isCancellingSubscription && isAdmin && (
            <button
              data-testid="cancelling-status-btn"
              onClick={() => setAssinaturaOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all bg-slate-100 border border-slate-200 text-slate-600 hover:bg-slate-200"
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              {subscriptionData?.current_period_end
                ? `Acesso até ${new Date(subscriptionData.current_period_end).toLocaleDateString("pt-BR")}`
                : cancelDaysLeft !== null ? `${cancelDaysLeft} dias restantes` : "Cancelamento agendado"}
              <span className="hidden sm:inline relative ml-1 px-2 py-0.5 rounded-md bg-emerald-500 text-white text-[10px] font-bold">
                Reativar
              </span>
            </button>
          )}

          {isOwner && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  data-testid="tenants-btn"
                  onClick={() => navigate("/owner/tenants")}
                  className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-400 relative transition-colors"
                >
                  <Building2 className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </TooltipTrigger>
              <TooltipContent>Gerenciar Tenants</TooltipContent>
            </Tooltip>
          )}
          <button
            data-testid="notifications-btn"
            onClick={() => setDrawerOpen(true)}
            className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-400 relative transition-colors"
          >
            <Bell className="w-5 h-5" strokeWidth={1.5} />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full flex items-center justify-center text-[10px] font-bold text-white px-1 leading-none"
                style={{ backgroundColor: tc500 }}>
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-btn" className="flex items-center gap-3 pl-3 border-l border-slate-200">
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium text-slate-900 leading-tight">{user?.name || "Usuario"}</p>
                  <p className="text-xs text-slate-500 leading-tight">{user?.email || ""}</p>
                </div>
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-sky-100 text-sky-700 text-xs font-semibold">{initials}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
                <p className="text-[10px] text-slate-400 font-mono mt-0.5">{user?.tenant_id}</p>
              </div>
              <DropdownMenuSeparator />
              {isAdmin && (
                <DropdownMenuItem
                  data-testid="subscription-btn"
                  onClick={() => setAssinaturaOpen(true)}
                  className="cursor-pointer gap-2"
                >
                  <CreditCard className="w-4 h-4" />
                  Minha Assinatura
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                data-testid="preferences-btn"
                onClick={() => setPrefsOpen(true)}
                className="cursor-pointer gap-2"
              >
                <Settings2 className="w-4 h-4" />
                {t("topbar.preferences")}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="changelog-btn"
                onClick={() => setChangelogOpen(true)}
                className="cursor-pointer gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Atualizações
                {hasNewChangelog && <span className="w-2 h-2 rounded-full bg-rose-500 ml-auto" />}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="logout-btn"
                onClick={logout}
                className="text-rose-600 focus:text-rose-700 focus:bg-rose-50 cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t("topbar.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <PreferenciasModal open={prefsOpen} onOpenChange={setPrefsOpen} />
      <AssinaturaModal open={assinaturaOpen} onOpenChange={setAssinaturaOpen} onSubscriptionChange={fetchSubscriptionData} />
      <ChangelogModal open={changelogOpen} onOpenChange={setChangelogOpen} />
      <NotificationDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCountChange={setUnreadCount}
      />

      {/* Trial expired grace modal */}
      <AlertDialog open={showExpiredModal} onOpenChange={setShowExpiredModal}>
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-600">
              <Clock className="w-5 h-5" />
              Seu trial expirou
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-500 space-y-2">
              <span className="block">Seu período de teste de 14 dias chegou ao fim.</span>
              <span className="block">
                {trial?.grace_days_left > 0
                  ? `Você ainda pode acessar o sistema por mais ${trial.grace_days_left} dia(s). Após isso, o acesso será bloqueado.`
                  : "Contrate agora para continuar usando o sistema."
                }
              </span>
              <span className="block font-medium text-slate-700">Seus dados estão preservados e seguros.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
            <AlertDialogAction style={{ backgroundColor: tc500 }} className="w-full text-white" onClick={() => { setShowExpiredModal(false); navigate("/checkout"); }}>
              <Sparkles className="w-4 h-4 mr-2" />Contratar agora
            </AlertDialogAction>
            <AlertDialogAction variant="outline" className="w-full bg-white border border-slate-200 text-slate-600 hover:bg-slate-50" onClick={() => { sessionStorage.setItem("trial_expired_dismissed", "1"); setShowExpiredModal(false); }}>
              Continuar explorando
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Trial blocked overlay */}
      {trial?.blocked && !location.pathname.startsWith("/checkout") && (
        <div className="fixed inset-0 z-[100] bg-white flex items-center justify-center overflow-y-auto py-8">
          <div className="max-w-lg w-full px-6">
            {/* Top: logout */}
            <div className="flex justify-end mb-6">
              <button
                data-testid="blocked-logout-btn"
                onClick={logout}
                className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" />Sair da conta
              </button>
            </div>

            {/* Main card */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
              <div className="p-6 text-center" style={{ background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)" }}>
                <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-7 h-7 text-white" />
                </div>
                <h1 className="text-xl font-bold text-white">Seu teste terminou</h1>
                <p className="text-blue-200 text-sm mt-1">Mas seus dados estão seguros e esperando por você.</p>
              </div>

              <div className="p-6 space-y-5">
                {/* Value props */}
                <div className="space-y-2.5">
                  {[
                    { icon: <BarChart3 className="w-4 h-4 text-blue-500" />, text: "Seus negócios, contatos e histórico continuam salvos" },
                    { icon: <Sparkles className="w-4 h-4 text-amber-500" />, text: "Reative em segundos — sem reconfigurar nada" },
                    { icon: <Lock className="w-4 h-4 text-emerald-500" />, text: "Acesso completo a todas as funcionalidades" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-lg px-4 py-2.5">
                      {item.icon}
                      <span className="text-sm text-slate-700">{item.text}</span>
                    </div>
                  ))}
                </div>

                {/* Pricing */}
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <p className="text-xs text-blue-600 font-medium mb-1">A partir de</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-3xl font-bold text-slate-900">R${tenantPricing ? tenantPricing.base_price.toFixed(2) : "49.00"}</span>
                    <span className="text-sm text-slate-500">/mês</span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">{tenantPricing ? tenantPricing.included_users : 1} usuário{(tenantPricing?.included_users || 1) > 1 ? "s" : ""} incluso{(tenantPricing?.included_users || 1) > 1 ? "s" : ""} · +R${tenantPricing ? tenantPricing.extra_user_price.toFixed(2) : "10.00"} por adicional</p>
                </div>

                <Button
                  data-testid="blocked-cta"
                  onClick={() => isCancellingSubscription ? setAssinaturaOpen(true) : navigate("/checkout")}
                  className="w-full h-12 text-white font-semibold text-base rounded-xl transition-all hover:opacity-90"
                  style={{ backgroundColor: isCancellingSubscription ? "#10b981" : "#3B82F6" }}
                >
                  <Sparkles className="w-5 h-5 mr-2" />{isCancellingSubscription ? "Reativar assinatura" : "Contratar agora"}
                </Button>

                <p className="text-[11px] text-slate-400 text-center">
                  Cancele quando quiser, sem fidelidade. Precisa de ajuda? <a href="mailto:contato@pipe4.me" className="text-blue-500 hover:underline">Fale conosco</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
